import { useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { glyphFor } from '../lib/assets.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import Icon from '../components/Icon.jsx'
import Modal from '../components/Modal.jsx'
import { PickerDropdown } from '../components/Dropdown.jsx'
import { DateField } from '../components/DatePicker.jsx'
import { CardHead, Switch, TableSearch } from '../components/SettingsLayout.jsx'
import { useToast } from '../components/Toast.jsx'
import { formatDate } from '../lib/dates.js'
import { toggleInSet } from '../lib/sets.js'

/**
 * The asset register — what the company owns, and how it is grouped.
 *
 * Read by anyone signed in, changed only by an owner or an asset admin. The server decides
 * that and says so in the payload's `canManage`; this page reads that one flag rather than
 * re-deriving the rule from roles, so the two can never disagree about what somebody may do.
 * Hiding a button is a courtesy, never the control — every write is refused server-side too.
 */

/**
 * The five kinds the product ships with, mapped to what they look like.
 *
 * The database stores the KEY. It says which kind of thing a group holds; this table decides
 * what that looks like, so a redesign is a change here rather than a data migration. Keep the
 * keys in step with the CHECK in migration 012 and ASSET_ICONS in repos/assets.js.
 */
// The glyphs come from lib/assets.js, which the board's bands read too — the labels are the
// picker's own business and live only here.
export const ASSET_ICONS = [
  // The stored key stays 'cars' — it is in the CHECK on asset_groups.icon and in every row
  // already using it, so renaming the label is a caption change and renaming the key would be
  // a data migration for no gain.
  { key: 'cars', label: 'Vehicles' },
  { key: 'tools', label: 'Tools' },
  { key: 'computers', label: 'Computers' },
  { key: 'phones', label: 'Phones' },
  { key: 'clothing', label: 'Clothing' },
].map((i) => ({ ...i, Glyph: glyphFor(i.key) }))

const iconFor = (key) => ASSET_ICONS.find((i) => i.key === key) || ASSET_ICONS[1]

function AssetIcon({ icon, size = 16 }) {
  const { Glyph } = iconFor(icon)
  return <Glyph size={size} strokeWidth={1.9} />
}

const STATUS_FILTERS = [
  { key: 'active', label: 'In service' },
  { key: 'retired', label: 'Retired' },
  { key: 'all', label: 'All' },
]

export function AssetManagement() {
  const qc = useQueryClient()
  // The company's own words for the register and for one item — "Fleet", "vehicle" — set
  // under Settings → Configure. Stored as "asset" regardless.
  const { assetsName, assetUnit } = useAuth()
  const unit = assetUnit()
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  // Which group sections are folded away. Empty to begin with: the page opens showing
  // everything, because "what do we own" is the question it exists to answer, and a page of
  // shut drawers answers it with a list of headings.
  const [shut, setShut] = useState(() => new Set())
  // Set when a new asset is being added, carrying the group it was started from.
  const [addingAsset, setAddingAsset] = useState(null) // { groupId } | null
  const [addingGroup, setAddingGroup] = useState(false)
  const [editingAsset, setEditingAsset] = useState(null)
  const [editingGroup, setEditingGroup] = useState(null)

  const { data } = useQuery({ queryKey: ['assets'], queryFn: () => api.get('/api/assets') })
  const refresh = () => qc.invalidateQueries({ queryKey: ['assets'] })
  const toast = useToast()

  const groups = data?.groups || []
  const canManage = Boolean(data?.canManage)
  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups])

  const visibleAssets = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (data?.assets || []).filter((a) => {
      if (statusFilter === 'active' && !a.active) return false
      if (statusFilter === 'retired' && a.active) return false
      if (!needle) return true
      // The group names too: "show me the vans" is a search people will type, and matching
      // only the asset's own name would answer it with nothing.
      const inGroups = a.groupIds.some((id) => groupsById.get(id)?.name.toLowerCase().includes(needle))
      return (
        a.name.toLowerCase().includes(needle) ||
        (a.description || '').toLowerCase().includes(needle) ||
        inGroups
      )
    })
  }, [data?.assets, q, statusFilter, groupsById])

  /**
   * One section per group, in the register's own order, and a closing section for anything in
   * no group at all. Assets and groups were two tabs; they are one list now, because the
   * groups ARE how the assets are arranged and flipping between them to see that was work.
   *
   * An asset in several groups is listed under each — the same as on the board, and for the
   * same reason: being in more than one is the point of grouping being many-to-many, and
   * listing it once would hide it from a group it genuinely belongs to.
   *
   * An empty group still gets its heading, so it can be added to. While a SEARCH is running
   * it drops out: an empty section answers "nothing here", and eight of those bury the one
   * section that matched.
   */
  const sections = useMemo(() => {
    const searching = Boolean(q.trim())
    const out = groups
      .map((g) => ({
        key: g.id,
        group: g,
        name: g.name,
        icon: g.icon,
        assets: visibleAssets.filter((a) => a.groupIds.includes(g.id)),
      }))
      .filter((sec) => !searching || sec.assets.length > 0)

    const loose = visibleAssets.filter((a) => a.groupIds.length === 0)
    if (loose.length) {
      out.push({ key: '__ungrouped__', group: null, name: 'Not in a group', icon: null, assets: loose })
    }
    return out
  }, [groups, visibleAssets, q])

  const toggle = (key) => setShut((prev) => toggleInSet(prev, key))

  // Up and down, one place at a time — the buttons Lists use. Off while a search is running:
  // with rows filtered out, "one place up" would be a place nobody can see.
  const searching = Boolean(q.trim())
  const reorderHint = searching ? 'Clear the search to change the order' : undefined

  async function sendOrder(path, ids) {
    try {
      await api.put(path, { ids })
      refresh()
      // The board's fleet band is drawn in the same order.
      qc.invalidateQueries({ queryKey: ['schedule'] })
    } catch (err) {
      toast.error(err)
    }
  }

  const swap = (ids, a, b) => {
    const i = ids.indexOf(a)
    const j = ids.indexOf(b)
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    return ids
  }

  function moveGroup(group, dir) {
    const ids = groups.map((g) => g.id)
    const other = ids[ids.indexOf(group.id) + dir]
    if (other) sendOrder('/api/asset-groups/reorder', swap(ids, group.id, other))
  }

  // Swapped with the neighbour on screen, but the whole group is sent, retired assets
  // included, so an asset hidden by the status filter keeps its place.
  function moveAsset(sec, asset, dir) {
    const i = sec.assets.findIndex((a) => a.id === asset.id)
    const other = sec.assets[i + dir]
    if (!other) return
    const ids = (data?.assets || [])
      .filter((a) => (sec.group ? a.groupIds.includes(sec.group.id) : a.groupIds.length === 0))
      .map((a) => a.id)
    sendOrder('/api/assets/reorder', swap(ids, asset.id, other.id))
  }

  return (
    <div className="card">
      <CardHead
        title={`${assetsName()} Management`}
        sub="Manage all your assets within CrewDispatch Service."
        action={
          canManage ? (
            <button className="btn primary" onClick={() => setAddingGroup(true)}>
              <Icon name="plus" size={16} /> Add group
            </button>
          ) : null
        }
      />

      <div className="filter-toolbar">
        <div className="segmented">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              className={statusFilter === f.key ? 'selected' : ''}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <TableSearch value={q} onChange={setQ} placeholder={`Search ${unit}s and groups…`} />
      </div>

      {sections.map((sec) => {
        const closed = shut.has(sec.key)
        return (
          <section className="asset-group-section" key={sec.key}>
            {/* The bar folds the section — the thing done to it most often. Editing the group
                is its own small button, so opening a drawer can never rename anything by
                accident. */}
            <div
              className={`asset-group-bar ${closed ? 'closed' : ''}`}
              onClick={() => toggle(sec.key)}
              role="button"
              tabIndex={0}
              aria-expanded={!closed}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle(sec.key)
                }
              }}
            >
              <button
                type="button"
                className={`collapse-btn ${closed ? 'collapsed' : ''}`}
                aria-label={closed ? `Show ${sec.name}` : `Collapse ${sec.name}`}
                onClick={(e) => { e.stopPropagation(); toggle(sec.key) }}
              >
                <Icon name="down" size={14} strokeWidth={2} />
              </button>
              {sec.icon ? <AssetIcon icon={sec.icon} /> : <span className="asset-group-nogroup" />}
              <span className="asset-group-title">{sec.name}</span>
              <span className="asset-group-count">{sec.assets.length}</span>

              <span className="asset-group-gap" />

              {/* Both at the right end, both quiet. Neither is the reason you came to this
                  page — that is reading it — so they sit out of the way of the title and
                  carry no colour that would compete with the one primary action above. */}
              {canManage && sec.group && (
                <>
                  <button
                    type="button"
                    className="icon-btn"
                    title={reorderHint || 'Move up'}
                    aria-label={`Move ${sec.name} up`}
                    disabled={searching || groups[0]?.id === sec.group.id}
                    onClick={(e) => { e.stopPropagation(); moveGroup(sec.group, -1) }}
                  >
                    <Icon name="up" size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    title={reorderHint || 'Move down'}
                    aria-label={`Move ${sec.name} down`}
                    disabled={searching || groups[groups.length - 1]?.id === sec.group.id}
                    onClick={(e) => { e.stopPropagation(); moveGroup(sec.group, 1) }}
                  >
                    <Icon name="down" size={15} />
                  </button>
                </>
              )}
              {canManage && sec.group && (
                <button
                  type="button"
                  className="btn asset-bar-btn"
                  title={`Rename ${sec.name}, change its icon, or delete it`}
                  onClick={(e) => { e.stopPropagation(); setEditingGroup(sec.group) }}
                >
                  <Icon name="edit" size={12} />
                  Edit
                </button>
              )}
              {canManage && (
                <button
                  type="button"
                  className="btn asset-bar-btn"
                  onClick={(e) => { e.stopPropagation(); setAddingAsset({ groupId: sec.group?.id || null }) }}
                >
                  <Icon name="plus" size={12} />
                  Add {unit}
                </button>
              )}
            </div>

            {!closed && (
              <table className={`data asset-group-table ${canManage ? 'has-order' : ''}`}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    {/* The standing operator as of today. The board can say otherwise for a day. */}
                    <th>Usually with</th>
                    {canManage && <th className="asset-order-col" aria-label="Order" />}
                  </tr>
                </thead>
                <tbody>
                  {sec.assets.map((a, i) => {
                    return (
                      <tr
                        key={a.id}
                        className={canManage ? 'row-click' : undefined}
                        onClick={canManage ? () => setEditingAsset(a) : undefined}
                      >
                        <td>
                          {a.name}
                          {!a.active && <span className="tag muted" style={{ marginLeft: 6 }}>Retired</span>}
                        </td>
                        <td className="muted">{a.description || '—'}</td>
                        <td className={a.standingStaffName ? '' : 'muted'}>
                          {a.standingStaffName || (a.standingSince ? 'Floater' : '—')}
                        </td>
                        {canManage && (
                          <td className="asset-order-col" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="icon-btn"
                              title={reorderHint || 'Move up'}
                              aria-label={`Move ${a.name} up`}
                              disabled={searching || i === 0}
                              onClick={() => moveAsset(sec, a, -1)}
                            >
                              <Icon name="up" size={14} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              title={reorderHint || 'Move down'}
                              aria-label={`Move ${a.name} down`}
                              disabled={searching || i === sec.assets.length - 1}
                              onClick={() => moveAsset(sec, a, 1)}
                            >
                              <Icon name="down" size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                  {sec.assets.length === 0 && (
                    <tr>
                      <td className="empty" colSpan={canManage ? 4 : 3}>Nothing in this group yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </section>
        )
      })}

      {sections.length === 0 && (
        <div className="empty">
          {q.trim() || statusFilter !== 'active'
            ? `No ${unit}s match those filters.`
            : 'No groups yet. Add one to start listing what the company owns.'}
        </div>
      )}

      {addingAsset && (
        <AssetModal
          mode="create"
          groups={groups}
          unit={unit}
          staff={data?.staff || []}
          presetGroupIds={addingAsset.groupId ? [addingAsset.groupId] : []}
          onClose={() => setAddingAsset(null)}
          onSaved={() => { setAddingAsset(null); refresh() }}
        />
      )}
      {editingAsset && (
        <AssetModal
          mode="edit"
          asset={editingAsset}
          groups={groups}
          unit={unit}
          staff={data?.staff || []}
          onRefresh={refresh}
          onClose={() => setEditingAsset(null)}
          onSaved={() => { setEditingAsset(null); refresh() }}
        />
      )}
      {addingGroup && (
        <AssetGroupModal
          mode="create"
          onClose={() => setAddingGroup(false)}
          onSaved={() => { setAddingGroup(false); refresh() }}
        />
      )}
      {editingGroup && (
        <AssetGroupModal
          mode="edit"
          group={editingGroup}
          onClose={() => setEditingGroup(null)}
          onSaved={() => { setEditingGroup(null); refresh() }}
        />
      )}
    </div>
  )
}

/** A date with a way to clear it. DateField itself has no empty state to reach for. */
function ClearableDate({ value, onChange, min, max, today, weekStart, label }) {
  return (
    <span className="fleet-date">
      <DateField value={value} onChange={onChange} min={min} max={max} today={today} weekStart={weekStart} label={label} />
      {value && (
        <button type="button" className="icon-btn" title="Clear" aria-label={`Clear ${label}`} onClick={() => onChange(null)}>
          <Icon name="close" size={13} strokeWidth={2.2} />
        </button>
      )}
    </span>
  )
}

/**
 * Who has this asset by default, from a date.
 *
 * No heading and no explanation: the switch above it already says "Set default user", and a
 * paragraph under a control that is one date and one name was more words than the thing it
 * described.
 *
 * The date comes first because it is read first — "from Monday, Marc" is the sentence. And
 * there is no Save: choosing the person IS the action, the way picking a job type is. A
 * button that only ever gets pressed immediately after the choice beside it is a second step
 * for nothing.
 *
 * The person list is inline rather than a floating panel. The dialog scrolls, and a panel
 * positioned against a control inside it hung 198px past the dialog's bottom edge and 13px
 * out of its left — clipped by the dialog's own overflow. Inline, the list is in the flow and
 * the dialog simply scrolls to it.
 */
function CustodySection({ asset = null, staff, today, weekStart, onPending }) {
  const qc = useQueryClient()
  const toast = useToast()
  // Before the asset exists there is no timeline to read: custody is keyed to an id, and the
  // id is minted by the save. So on create this collects the choice and hands it up, and the
  // dialog writes it the moment the asset has an id — see AssetModal's save.
  const pending = !asset
  const { data } = useQuery({
    queryKey: ['asset-custody', asset?.id],
    queryFn: () => api.get(`/api/assets/${asset.id}/custody`),
    enabled: !pending,
  })
  const [from, setFrom] = useState(today)
  const [text, setText] = useState(() => (asset?.standingStaffId ? asset.standingStaffName || '' : ''))
  const [picked, setPicked] = useState(asset?.standingStaffId || '')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef(null)

  const matches = useMemo(() => {
    if (picked) return []
    const q = text.trim().toLowerCase()
    return staff.filter((s) => !q || s.name.toLowerCase().includes(q)).slice(0, 3)
  }, [staff, text, picked])
  const showList = open && !picked && matches.length > 0

  async function commit(person) {
    if (pending) {
      onPending?.({ staffUserId: person?.id || null, effectiveFrom: from })
      return
    }
    setBusy(true)
    try {
      await api.post(`/api/assets/${asset.id}/custody`, {
        staffUserId: person?.id || null,
        effectiveFrom: from,
      })
      qc.invalidateQueries({ queryKey: ['asset-custody', asset.id] })
      qc.invalidateQueries({ queryKey: ['assets'] })
      qc.invalidateQueries({ queryKey: ['schedule'] })
      toast.success(
        person
          ? `${person.name} usually has ${asset.name} from ${formatDate(from)}.`
          : `${asset.name} is a floater from ${formatDate(from)}.`,
      )
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const choose = (person) => {
    setPicked(person.id)
    setText(person.name)
    setOpen(false)
    setActive(-1)
    commit(person)
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((i) => Math.min(i + 1, matches.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, -1))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (showList && active >= 0) return choose(matches[active])
      // Only a real person can hold an asset by default — there is no outside party here the
      // way there is on a single day of the board, so a name that matches nobody does nothing.
      const exact = staff.find((s) => s.name.toLowerCase() === text.trim().toLowerCase())
      if (exact) choose(exact)
    }
  }

  return (
    <div className="field custody-block">
      <div className="custody-line">
        <span className="custody-word">from</span>
        <DateField
          value={from}
          onChange={(d) => {
            setFrom(d)
            // On create the date is half of a choice not yet written, so a change to it has
            // to reach the dialog too — otherwise picking the person first and the date
            // second would save the person against today.
            if (pending && picked) {
              onPending?.({ staffUserId: picked, effectiveFrom: d })
            }
          }}
          today={today}
          weekStart={weekStart}
          label="Default from"
        />
      </div>

      <div className="ac custody-who">
        <input
          ref={inputRef}
          className="cell-pop-input"
          type="text"
          maxLength={120}
          value={text}
          disabled={busy}
          placeholder="Who usually has it?"
          autoComplete="off"
          role="combobox"
          aria-expanded={showList}
          aria-autocomplete="list"
          onChange={(e) => { setText(e.target.value); setPicked(''); setActive(-1); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {picked && (
          <button
            type="button"
            className="ac-clear"
            aria-label="Clear"
            onClick={() => { setPicked(''); setText(''); setOpen(true); inputRef.current?.focus() }}
          >
            <Icon name="close" size={12} strokeWidth={2.4} />
          </button>
        )}
        {showList && (
          <ul className="ac-list" role="listbox">
            {matches.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  className={`ac-option ${i === active ? 'active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); choose(s) }}
                  onMouseEnter={() => setActive(i)}
                >
                  <span className="ac-option-name">{s.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Every decision made about this asset, most recent first — the register keeps the
          history rather than overwriting it, which is what lets a change from a date leave
          the earlier days alone. */}
      {!pending && data?.custody?.length > 0 && (
        <ul className="fleet-custody-history">
          {data.custody.map((c) => (
            <li key={c.id}>
              <span className="fleet-custody-date">{formatDate(c.effectiveFrom, { withYear: true })}</span>
              <span>{c.staffName || 'Floater — nobody'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Exported so the board's fleet band can open the very same dialog from a right-click on an
// asset's name, rather than growing a second, drifting copy of it.
export function AssetModal({ mode, asset, groups, unit = 'asset', staff = [], presetGroupIds = null, onClose, onSaved, onRefresh }) {
  const isEdit = mode === 'edit'
  const { mode: liveMode, tenant } = useAuth()
  const today = liveMode?.today
  const weekStart = tenant?.weekStart || 1
  const [name, setName] = useState(asset?.name || '')
  const [description, setDescription] = useState(asset?.description || '')
  // The service window, both ends optional: a rental's delivery and return days. Distinct
  // from Retire below, which takes the asset off every day of the board including the past.
  const [inServiceFrom, setInServiceFrom] = useState(asset?.inServiceFrom || null)
  const [inServiceTo, setInServiceTo] = useState(asset?.inServiceTo || null)
  /**
   * Whether this asset has a service window at all.
   *
   * The two dates were on screen for every asset, and for nearly every asset the answer is
   * "we have always had it and still do" — two empty date fields asking a question that does
   * not apply. Behind a switch, the common case is silent and the rental says so out loud.
   *
   * Opens already on for an asset that has one, since that IS what those dates mean.
   */
  const [temporary, setTemporary] = useState(Boolean(asset?.inServiceFrom || asset?.inServiceTo))
  // Creating from a group's own header arrives with that group already ticked — which is the
  // whole point of adding it from there rather than from the top of the page.
  // One group, not a set. Membership is still sent as a list because that is the shape the
  // server and the board read, but the choice itself is single now.
  const [groupId, setGroupId] = useState(
    () => (asset?.groupIds || presetGroupIds || [])[0] || '',
  )
  // Same idea for the standing operator: most assets do not have one, and the picker and its
  // history were permanently open asking. On for an asset that already has one.
  const [wantsDefault, setWantsDefault] = useState(Boolean(asset?.standingStaffId))
  // On create, the operator chosen before the asset exists — written immediately after it
  // does. { staffUserId, effectiveFrom } | null.
  const [pendingCustody, setPendingCustody] = useState(null)
  // Local, so Retire/Restore updates the pill without closing — `asset` is the snapshot from
  // the moment this opened.
  const [active, setActive] = useState(asset?.active ?? true)
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const toast = useToast()

  // The groups this asset is in, for the strip across the top. Whichever it was opened from
  // is simply the first of them.
  const chosen = groups.find((g) => g.id === groupId) || null

  async function save(e) {
    e.preventDefault()
    setBusy(true)
    // Switching Temporary off clears the window rather than hiding it — a date that no longer
    // shows must not keep taking the asset off the board.
    const body = {
      name,
      description,
      groupIds: groupId ? [groupId] : [],
      inServiceFrom: temporary ? inServiceFrom : null,
      inServiceTo: temporary ? inServiceTo : null,
    }
    try {
      if (isEdit) {
        await api.patch(`/api/assets/${asset.id}`, body)
      } else {
        const created = await api.post('/api/assets', body)
        // The default operator, now that there is an id to hang it on. Deliberately after
        // the asset is created and NOT rolled back if it fails: the asset is the thing being
        // added, and losing it because the operator did not take would be the worse trade.
        // The failure is reported and the operator can be set from the row.
        if (wantsDefault && pendingCustody?.staffUserId && created?.asset?.id) {
          try {
            await api.post(`/api/assets/${created.asset.id}/custody`, pendingCustody)
          } catch (err) {
            toast.error(err)
          }
        }
      }
      onSaved()
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  async function toggleActive() {
    const next = !active
    try {
      await api.patch(`/api/assets/${asset.id}`, { active: next })
      setActive(next)
      onRefresh?.()
    } catch (err) {
      toast.error(err)
    }
  }

  // Deleting is for an entry that was never used — a rental that fell through. Anything with
  // history can only be retired, and the server holds that line whatever this says; the
  // button stays visible, disabled with the reason, because it is exactly what somebody
  // tidying the register will look for.
  async function remove() {
    setBusy(true)
    try {
      await api.del(`/api/assets/${asset.id}/permanently`)
      toast.success(`${asset.name} deleted.`)
      onSaved()
    } catch (err) {
      toast.error(err)
      setBusy(false)
      setConfirmDelete(false)
    }
  }

  return (
    <Modal title={`${isEdit ? 'Edit' : 'Add'} ${unit}`} onClose={onClose} width={480}>
      <form onSubmit={save}>
        {/* One control, not two. Which groups this is in was shown at the top and CHANGED
            in a field further down, which is two places for one fact — so the strip at the
            top is itself the picker. Opened from a group's own Add button it already reads
            that group. */}
        <div className="asset-modal-groups">
          <PickerDropdown
            align="left"
            value={groupId}
            onPick={(v) => setGroupId(v || '')}
            searchable={groups.length > 8}
            options={[
              { value: '', label: 'In no group' },
              ...groups.map((g) => ({ value: g.id, label: g.name })),
            ]}
            renderTrigger={() =>
              chosen ? (
                <span className="asset-modal-group">
                  <AssetIcon icon={chosen.icon} size={14} />
                  {chosen.name}
                </span>
              ) : (
                <span className="asset-modal-group none">
                  {groups.length ? 'In no group' : 'No groups yet — add one first'}
                </span>
              )
            }
          />
        </div>

        <div className="field">
          <label>
            Name
            {isEdit && !active && <span className="tag muted" style={{ marginLeft: 6 }}>Retired</span>}
          </label>
          <input
            type="text"
            required
            autoFocus
            maxLength={120}
            placeholder="Ford Transit — unit 12"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="asset-desc">Description <span className="hint">optional</span></label>
          <textarea
            id="asset-desc"
            rows={3}
            maxLength={2000}
            placeholder="Anything worth knowing — plate, serial, where it lives."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="asset-toggle-row">
          <div className="asset-toggle-text">
            <span className="asset-toggle-label">Temporary {unit}</span>
            <span className="hint">Used for rentals or temporary assets</span>
          </div>
          <Switch checked={temporary} onChange={setTemporary} />
        </div>
        {temporary && (
          <div className="field asset-toggle-body">
            <div className="fleet-custody-row">
              <span className="fleet-custody-from">from</span>
              <ClearableDate value={inServiceFrom} onChange={setInServiceFrom} max={inServiceTo || undefined} today={today} weekStart={weekStart} label="On the fleet from" />
              <span className="fleet-custody-from">to</span>
              <ClearableDate value={inServiceTo} onChange={setInServiceTo} min={inServiceFrom || undefined} today={today} weekStart={weekStart} label="On the fleet to" />
            </div>
            <span className="hint">Leave either end blank for open-ended.</span>
          </div>
        )}

        <div className="asset-toggle-row">
          <div className="asset-toggle-text">
            <span className="asset-toggle-label">Set default user</span>
            <span className="hint">Who has this every day, unless a day says otherwise</span>
          </div>
          <Switch checked={wantsDefault} onChange={setWantsDefault} />
        </div>
        {wantsDefault && (
          <CustodySection
            asset={isEdit ? asset : null}
            staff={staff}
            today={today}
            weekStart={weekStart}
            onPending={setPendingCustody}
          />
        )}

        {confirmDelete && (
          <div className="notice">
            Delete “{asset.name}”? It has never been used, so only its entry goes. This cannot be undone.
          </div>
        )}

        <div className="modal-actions" style={{ marginTop: 20 }}>
          {/* Retired, not deleted, once it has a past: an asset that has been sold still has
              to make sense of the history that mentions it. */}
          {isEdit && (
            <button type="button" className="btn" onClick={toggleActive}>
              {active ? 'Retire' : 'Restore'}
            </button>
          )}
          {isEdit && (
            <button
              type="button"
              className={`btn ${confirmDelete ? 'danger' : 'ghost danger'}`}
              disabled={busy || asset.hasHistory}
              title={
                asset.hasHistory
                  ? `This ${unit} has been used on the board, so it can be retired but not deleted.`
                  : undefined
              }
              onClick={() => (confirmDelete ? remove() : setConfirmDelete(true))}
            >
              <Icon name="trash" size={14} />
              {confirmDelete ? 'Yes, delete it' : 'Delete'}
            </button>
          )}
          <span className="spacer" />
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy}>
            {isEdit ? 'Save changes' : `Add ${unit}`}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function AssetGroupModal({ mode, group, onClose, onSaved }) {
  const isEdit = mode === 'edit'
  const [name, setName] = useState(group?.name || '')
  const [icon, setIcon] = useState(group?.icon || 'tools')
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const toast = useToast()

  async function save(e) {
    e.preventDefault()
    setBusy(true)
    try {
      if (isEdit) await api.patch(`/api/asset-groups/${group.id}`, { name, icon })
      else await api.post('/api/asset-groups', { name, icon })
      onSaved()
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      await api.del(`/api/asset-groups/${group.id}`)
      onSaved()
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  return (
    <Modal title={isEdit ? 'Edit group' : 'Add group'} onClose={onClose} width={420}>
      <form onSubmit={save}>
        <div className="field">
          <label>Name</label>
          <input
            type="text"
            required
            autoFocus
            maxLength={120}
            placeholder="Vans"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* A row of glyphs rather than a dropdown of words: the thing being chosen IS a
            picture, and picking it from a list of names means imagining each one. */}
        <div className="field">
          <label>Icon</label>
          <div className="asset-icon-row">
            {ASSET_ICONS.map(({ key, label, Glyph }) => (
              <button
                key={key}
                type="button"
                className={`asset-icon-btn ${icon === key ? 'on' : ''}`}
                onClick={() => setIcon(key)}
                aria-pressed={icon === key}
                title={label}
              >
                <Glyph size={20} strokeWidth={1.8} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {confirmDelete ? (
          <div className="notice">
            Delete “{group.name}”? Its assets are kept — they simply stop being in this group.
          </div>
        ) : null}

        <div className="modal-actions" style={{ marginTop: 20 }}>
          {/* Deleted outright rather than retired: a group is a way of looking at assets, not
              a record of anything that happened, so nothing refers back to a dead one. */}
          {isEdit && (
            <button
              type="button"
              className={`btn ${confirmDelete ? 'danger' : 'ghost danger'}`}
              onClick={() => (confirmDelete ? remove() : setConfirmDelete(true))}
              disabled={busy}
            >
              <Icon name="trash" size={14} />
              {confirmDelete ? 'Yes, delete it' : 'Delete'}
            </button>
          )}
          <span className="spacer" />
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy}>
            {isEdit ? 'Save changes' : 'Add group'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
